import { ChangeEvent, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { createExpressContact, updateExpressContact } from "../../services/expressMaster";
import { ExpressContact } from "../../types/express-master.type";
import { TAX_ID_LENGTH, sanitizeInteger } from "../CreditDebitNote/noteDraft";
import MasterDialog from "./MasterDialog";
import {
  CONTACT_ADDRESS_MAX_LENGTH,
  CONTACT_KINDS,
  CONTACT_KIND_LABEL,
  CONTACT_PHONE_MAX_LENGTH,
  CONTACT_TITLE_MAX_LENGTH,
  MASTER_CODE_MAX_LENGTH,
  MASTER_NAME_MAX_LENGTH,
} from "./constants";
import {
  ContactForm,
  ContactFormErrors,
  EMPTY_CONTACT_FORM,
  contactFormFrom,
  toContactRequest,
  validateContactForm,
} from "./masterForm";

interface ContactDialogProps {
  open: boolean;
  /** The contact being edited; null to add one. */
  contact: ExpressContact | null;
  onClose: () => void;
  onSaved: () => void;
}

type ContactTextKey = Exclude<keyof ContactForm, "kind" | "isActive">;

interface ContactTextField {
  key: ContactTextKey;
  label: string;
  size: number;
  maxLength?: number;
  required?: boolean;
  multiline?: boolean;
  /** The validation message the field shows, for the fields that have one. */
  errorKey?: keyof ContactFormErrors;
  /** Keeps what is typed to what the server accepts. */
  sanitize?: (text: string) => string;
}

const toUpperCase = (text: string) => text.toUpperCase();
const toTaxId = (text: string) => sanitizeInteger(text).slice(0, TAX_ID_LENGTH);

const TEXT_FIELDS: ContactTextField[] = [
  { key: "title", label: "คำนำหน้า", size: 4, maxLength: CONTACT_TITLE_MAX_LENGTH },
  { key: "name", label: "ชื่อ", size: 8, maxLength: MASTER_NAME_MAX_LENGTH, required: true, errorKey: "name" },
  { key: "taxId", label: "เลขประจำตัวผู้เสียภาษี", size: 8, errorKey: "taxId", sanitize: toTaxId },
  { key: "taxBranchNo", label: "สาขา#", size: 4, sanitize: sanitizeInteger },
  { key: "address", label: "ที่อยู่", size: 12, maxLength: CONTACT_ADDRESS_MAX_LENGTH, multiline: true },
  { key: "phone", label: "โทร.", size: 8, maxLength: CONTACT_PHONE_MAX_LENGTH },
  { key: "creditDays", label: "เครดิต (วัน)", size: 4, sanitize: sanitizeInteger },
];

const CODE_FIELD: ContactTextField = {
  key: "code",
  label: "รหัส",
  size: 8,
  maxLength: MASTER_CODE_MAX_LENGTH,
  required: true,
  errorKey: "code",
  sanitize: toUpperCase,
};

function ContactTextInput({
  field,
  value,
  errors,
  onChangeField,
}: Readonly<{
  field: ContactTextField;
  value: string;
  errors: ContactFormErrors;
  onChangeField: (key: ContactTextKey, value: string) => void;
}>) {
  const error = field.errorKey ? errors[field.errorKey] : undefined;
  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChangeField(field.key, field.sanitize ? field.sanitize(event.target.value) : event.target.value);

  return (
    <Grid size={{ xs: 12, sm: field.size }}>
      <TextField
        fullWidth
        size="small"
        label={field.label}
        required={field.required}
        multiline={field.multiline}
        value={value}
        onChange={handleChange}
        error={Boolean(error)}
        helperText={error}
        slotProps={{ htmlInput: { maxLength: field.maxLength } }}
      />
    </Grid>
  );
}

/** Adds or edits a supplier / pseudo customer of the Express company database. */
export default function ContactDialog({ open, contact, onClose, onSaved }: Readonly<ContactDialogProps>) {
  const [form, setForm] = useState<ContactForm>(EMPTY_CONTACT_FORM);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    setForm(contact ? contactFormFrom(contact) : EMPTY_CONTACT_FORM);
    setErrors({});
  }, [open, contact]);

  const handleChangeField = (key: ContactTextKey, value: string) =>
    setForm((previous) => ({ ...previous, [key]: value }));
  const handleKindChange = (event: ChangeEvent<HTMLInputElement>) =>
    setForm((previous) => ({
      ...previous,
      kind: CONTACT_KINDS.find((kind) => kind === event.target.value) ?? previous.kind,
    }));
  const handleActiveChange = (isActive: boolean) => setForm((previous) => ({ ...previous, isActive }));

  const handleSave = async () => {
    const formErrors = validateContactForm(form);
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) return;
    setSaving(true);
    try {
      const body = toContactRequest(form);
      if (contact) await updateExpressContact(contact.uuid, body);
      else await createExpressContact(body);
      onSaved();
    } catch {
      // The interceptor already shows the server's reason (e.g. a code already used).
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterDialog
      open={open}
      title={contact ? "แก้ไขผู้จำหน่าย / ลูกค้า" : "เพิ่มผู้จำหน่าย / ลูกค้า"}
      saving={saving}
      isActive={form.isActive}
      onActiveChange={handleActiveChange}
      onClose={onClose}
      onSave={handleSave}
    >
      <Grid container spacing={2}>
        <ContactTextInput
          field={CODE_FIELD}
          value={form.code}
          errors={errors}
          onChangeField={handleChangeField}
        />
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField select fullWidth size="small" label="ประเภท" value={form.kind} onChange={handleKindChange}>
            {CONTACT_KINDS.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {CONTACT_KIND_LABEL[kind]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {TEXT_FIELDS.map((field) => (
          <ContactTextInput
            key={field.key}
            field={field}
            value={form[field.key]}
            errors={errors}
            onChangeField={handleChangeField}
          />
        ))}
      </Grid>
    </MasterDialog>
  );
}
